'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState } from 'react';
import { useProps, ColorSwatch, Popover, SimpleGrid, ColorPicker, Tooltip, Group, ActionIcon, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconX, IconCircleOff, IconColorPicker, IconPalette, IconCheck } from '../icons/Icons.mjs';
import { useRichTextEditorContext } from '../RichTextEditor.context.mjs';
import { RichTextEditorControl } from './RichTextEditorControl.mjs';

const defaultProps = {};
const RichTextEditorColorPickerControl = forwardRef((props, ref) => {
  const { popoverProps, colors, colorPickerProps, ...others } = useProps(
    "RichTextEditorColorPickerControl",
    defaultProps,
    props
  );
  const { editor, labels, getStyles, variant } = useRichTextEditorContext();
  const [opened, { toggle, close }] = useDisclosure(false);
  const [state, setState] = useState("palette");
  const currentColor = editor?.getAttributes("textStyle").color || "var(--mantine-color-text)";
  const handleChange = (value, shouldClose = true) => {
    (editor?.chain()).focus().setColor(value).run();
    shouldClose && close();
  };
  const handleClear = () => {
    (editor?.chain()).focus().unsetColor().run();
    close();
  };
  const controls = colors.map((color, index) => /* @__PURE__ */ jsx(
    ColorSwatch,
    {
      component: "button",
      color,
      onClick: () => handleChange(color),
      size: 26,
      radius: "xs",
      style: { cursor: "pointer" },
      title: labels.colorPickerColorLabel(color),
      "aria-label": labels.colorPickerColorLabel(color)
    },
    index
  ));
  return /* @__PURE__ */ jsxs(
    Popover,
    {
      opened,
      withinPortal: true,
      trapFocus: true,
      onChange: (_opened) => !_opened && close(),
      ...popoverProps,
      children: [
        /* @__PURE__ */ jsx(Popover.Target, { children: /* @__PURE__ */ jsx(
          RichTextEditorControl,
          {
            ...others,
            variant,
            "aria-label": labels.colorPickerControlLabel,
            title: labels.colorPickerControlLabel,
            ref,
            onClick: toggle,
            children: /* @__PURE__ */ jsx(ColorSwatch, { color: currentColor, size: 14 })
          }
        ) }),
        /* @__PURE__ */ jsxs(Popover.Dropdown, { ...getStyles("linkEditorDropdown"), children: [
          state === "palette" && /* @__PURE__ */ jsx(SimpleGrid, { cols: 7, spacing: 2, children: controls }),
          state === "colorPicker" && /* @__PURE__ */ jsx(
            ColorPicker,
            {
              defaultValue: currentColor,
              onChange: (value) => handleChange(value, false),
              ...colorPickerProps
            }
          ),
          /* @__PURE__ */ jsx(Tooltip.Group, { closeDelay: 200, children: /* @__PURE__ */ jsxs(Group, { justify: "flex-end", gap: "xs", mt: "sm", children: [
            state === "palette" && /* @__PURE__ */ jsx(
              ActionIcon,
              {
                variant: "default",
                onClick: close,
                title: labels.colorPickerCancel,
                "aria-label": labels.colorPickerCancel,
                children: /* @__PURE__ */ jsx(IconX, { style: { width: rem(16), height: rem(16) } })
              }
            ),
            /* @__PURE__ */ jsx(
              ActionIcon,
              {
                variant: "default",
                onClick: handleClear,
                title: labels.colorPickerClear,
                "aria-label": labels.colorPickerClear,
                children: /* @__PURE__ */ jsx(IconCircleOff, { style: { width: rem(16), height: rem(16) } })
              }
            ),
            state === "palette" ? /* @__PURE__ */ jsx(
              ActionIcon,
              {
                variant: "default",
                onClick: () => setState("colorPicker"),
                title: labels.colorPickerColorPicker,
                "aria-label": labels.colorPickerColorPicker,
                children: /* @__PURE__ */ jsx(IconColorPicker, { style: { width: rem(16), height: rem(16) } })
              }
            ) : /* @__PURE__ */ jsx(
              ActionIcon,
              {
                variant: "default",
                onClick: () => setState("palette"),
                "aria-label": labels.colorPickerPalette,
                title: labels.colorPickerPalette,
                children: /* @__PURE__ */ jsx(IconPalette, { style: { width: rem(16), height: rem(16) } })
              }
            ),
            state === "colorPicker" && /* @__PURE__ */ jsx(
              ActionIcon,
              {
                variant: "default",
                onClick: close,
                title: labels.colorPickerSave,
                "aria-label": labels.colorPickerSave,
                children: /* @__PURE__ */ jsx(IconCheck, { style: { width: rem(16), height: rem(16) } })
              }
            )
          ] }) })
        ] })
      ]
    }
  );
});
RichTextEditorColorPickerControl.displayName = "@mantine/tiptap/ColorPickerControl";

export { RichTextEditorColorPickerControl };
//# sourceMappingURL=RichTextEditorColorPickerControl.mjs.map
